/**
 * /admin/attraction-edits — Pending partner-submitted listing edits.
 *
 * The admin sees a diff view (submitted value vs current live value) per
 * field so they can approve or reject with a note. Approvals merge the
 * changes into the live attraction; rejections email the partner the reason.
 */
import React, { useEffect, useState } from 'react';
import { Check, Clock, Loader2, X } from 'lucide-react';
import api from '../lib/api';

export default function AdminAttractionEdits() {
  const [statusFilter, setStatusFilter] = useState('pending');
  const [edits, setEdits] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [busy, setBusy] = useState(null);
  const [note, setNote] = useState('');

  async function load() {
    setEdits(null);
    const { data } = await api.get(`/admin/attraction-edits?status=${statusFilter}`);
    setEdits(data.items || []);
  }
  useEffect(() => { load(); }, [statusFilter]);

  async function approve(id) {
    setBusy(id);
    try { await api.post(`/admin/attraction-edits/${id}/approve`, { note }); }
    finally { setBusy(null); setNote(''); setExpanded(null); load(); }
  }
  async function reject(id) {
    if (!note.trim()) { window.alert('Please enter a reason so the partner knows what to fix.'); return; }
    setBusy(id);
    try { await api.post(`/admin/attraction-edits/${id}/reject`, { note }); }
    finally { setBusy(null); setNote(''); setExpanded(null); load(); }
  }

  return (
    <div className="p-6 sm:p-8 max-w-5xl">
      <h1 className="font-serif text-3xl text-heritage mb-2">Partner listing edits</h1>
      <p className="text-sm text-charcoal/70 mb-5">Partners edit their own listings from <code>/partners/my-listing</code>. Everything they change lands here for approval.</p>

      <div className="flex gap-2 mb-5">
        {['pending', 'approved', 'rejected', 'all'].map((s) => (
          <button key={s} onClick={() => setStatusFilter(s)}
                  data-testid={`edits-filter-${s}`}
                  className={`px-4 py-1.5 rounded-full text-sm font-semibold ${statusFilter === s ? 'bg-heritage text-cream' : 'bg-admin/40 text-charcoal border border-charcoal/10'}`}>
            {s.charAt(0).toUpperCase() + s.slice(1)}
          </button>
        ))}
      </div>

      {edits === null ? <Loader2 className="animate-spin"/> :
       edits.length === 0 ? <p className="text-sm text-charcoal/60">Nothing to show — the queue is empty.</p> :
      (
        <div className="space-y-4">
          {edits.map((e) => {
            const current = e.current || {};
            const isOpen = expanded === e.id;
            return (
              <div key={e.id} className="bg-white border border-charcoal/10 rounded p-4" data-testid={`edit-row-${e.id.slice(0,6)}`}>
                <div className="flex justify-between items-start gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold inline-flex items-center gap-2">
                      {e.partner_business_name || current.name}
                      {e.source === 'ai_launch_draft' && (
                        <span className="text-[10px] uppercase tracking-wider bg-heritage/10 text-heritage px-2 py-0.5 rounded-full font-bold" data-testid={`edit-ai-label-${e.id.slice(0,6)}`}>✨ AI starter draft</span>
                      )}
                    </div>
                    <div className="text-xs text-charcoal/60 mt-0.5 truncate">
                      {e.partner_email} · Submitted {new Date(e.submitted_at).toLocaleString('en-GB')} · Fields: {Object.keys(e.changes || {}).join(', ')}
                    </div>
                    {e.status !== 'pending' && (
                      <div className="text-xs text-charcoal/50 mt-1">{e.status} by {e.reviewed_by} at {e.reviewed_at ? new Date(e.reviewed_at).toLocaleString('en-GB') : ''}{e.admin_note ? ` — "${e.admin_note}"` : ''}</div>
                    )}
                  </div>
                  <div className="shrink-0 flex gap-2">
                    <button onClick={() => setExpanded(isOpen ? null : e.id)} className="text-xs px-3 py-1.5 rounded-full border border-charcoal/15 hover:bg-admin/40" data-testid={`edit-toggle-${e.id.slice(0,6)}`}>
                      {isOpen ? 'Close' : 'Review'}
                    </button>
                    {e.status === 'pending' && !isOpen && (
                      <span className="text-xs px-2 py-1 rounded-full bg-mustard/20 text-charcoal inline-flex items-center gap-1"><Clock size={11}/> Pending</span>
                    )}
                  </div>
                </div>

                {isOpen && (
                  <div className="mt-4 border-t border-charcoal/10 pt-4 space-y-3">
                    {Object.entries(e.changes || {}).map(([field, newVal]) => (
                      <FieldDiff key={field} field={field} newVal={newVal} oldVal={current[field]}/>
                    ))}
                    {e.note && <p className="text-xs italic text-charcoal/60">Partner note: &ldquo;{e.note}&rdquo;</p>}

                    {e.status === 'pending' && (
                      <div className="mt-4 space-y-2">
                        <input value={note} onChange={(ev) => setNote(ev.target.value)} placeholder="Note to partner (required on reject)"
                               className="w-full border border-charcoal/20 rounded px-3 py-2 text-sm" data-testid={`edit-note-${e.id.slice(0,6)}`}/>
                        <div className="flex gap-2">
                          <button onClick={() => approve(e.id)} disabled={busy === e.id} data-testid={`edit-approve-${e.id.slice(0,6)}`}
                                  className="bg-forest text-cream px-5 py-2 rounded-full text-sm font-semibold inline-flex items-center gap-1.5 disabled:opacity-50">
                            {busy === e.id ? <Loader2 className="animate-spin" size={13}/> : <Check size={13}/>} Approve &amp; publish
                          </button>
                          <button onClick={() => reject(e.id)} disabled={busy === e.id} data-testid={`edit-reject-${e.id.slice(0,6)}`}
                                  className="bg-heritage text-cream px-5 py-2 rounded-full text-sm font-semibold inline-flex items-center gap-1.5 disabled:opacity-50">
                            <X size={13}/> Reject with note
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function FieldDiff({ field, newVal, oldVal }) {
  const isImg = field === 'hero_image' || field === 'gallery';
  const stringify = (v) => {
    if (v == null || v === '') return <em className="text-charcoal/40">(empty)</em>;
    if (Array.isArray(v)) return `[${v.length} item${v.length === 1 ? '' : 's'}]`;
    if (typeof v === 'object') return JSON.stringify(v);
    return String(v);
  };
  return (
    <div className="text-sm">
      <div className="text-xs font-semibold uppercase tracking-wide text-charcoal/60 mb-1">{field}</div>
      <div className="grid sm:grid-cols-2 gap-3">
        <div className="bg-admin/20 border border-charcoal/10 rounded p-2">
          <div className="text-xs text-charcoal/50 mb-1">Current</div>
          <div className="whitespace-pre-wrap break-words text-charcoal/80">
            {isImg && field === 'hero_image' && oldVal ? <img src={oldVal} alt="" className="max-h-24 rounded"/> :
             isImg && field === 'gallery' && Array.isArray(oldVal) ? (
               <div className="flex flex-wrap gap-1">{(oldVal || []).map((u) => <img key={u} src={u} alt="" className="w-14 h-14 object-cover rounded"/>)}</div>
             ) : stringify(oldVal)}
          </div>
        </div>
        <div className="bg-forest/10 border border-forest/30 rounded p-2">
          <div className="text-xs text-forest mb-1 font-semibold">Proposed</div>
          <div className="whitespace-pre-wrap break-words text-charcoal">
            {isImg && field === 'hero_image' && newVal ? <img src={newVal} alt="" className="max-h-24 rounded"/> :
             isImg && field === 'gallery' && Array.isArray(newVal) ? (
               <div className="flex flex-wrap gap-1">{(newVal || []).map((u) => <img key={u} src={u} alt="" className="w-14 h-14 object-cover rounded"/>)}</div>
             ) : stringify(newVal)}
          </div>
        </div>
      </div>
    </div>
  );
}
